import { useMutation, useQuery, useQueryClient } from "react-query";
import z from "zod";

import { zodParseWithLog } from "../apiErrorLogs";
import { sureApiCall } from "../axiosClient/sureApi";

const TransformationReferenceAdminSchema = z.object({
    "@id": z.string().optional(),
    id: z.number(),
    code: z.string(),
    name: z.string(),
    ecobalyseProcessUuid: z.string().nullable().optional(),
    ssq: z
        .union([z.string(), z.object({ "@id": z.string().optional(), id: z.number().optional() })])
        .nullable()
        .optional(),
    sourceVersion: z
        .union([z.string(), z.object({ "@id": z.string().optional(), id: z.number().optional() })])
        .nullable()
        .optional(),
    scores: z.array(z.unknown()).optional(),
});

export type TransformationReferenceAdmin = z.infer<typeof TransformationReferenceAdminSchema>;

export type TransformationReferenceWrite = {
    code: string;
    name: string;
    ecobalyseProcessUuid?: string | null;
    ssq?: string | null;
    sourceVersion?: string | null;
};

export const transformationReferencesAdminQueryKey = ["transformationReferencesAdmin"] as const;

export const useTransformationReferencesAdmin = () => {
    const query = useQuery({
        queryKey: transformationReferencesAdminQueryKey,
        queryFn: async () => {
            return await sureApiCall.get("/api/transformation_references", {
                params: { itemsPerPage: 200 },
            });
        },
        select: (request) => {
            return zodParseWithLog(
                request.data["hydra:member"],
                TransformationReferenceAdminSchema.array(),
                "transformationReferencesAdmin",
            );
        },
    });

    return {
        transformationReferences: query.data ?? [],
        ...query,
    };
};

export const useCreateTransformationReference = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: TransformationReferenceWrite) => {
            return await sureApiCall.post("/api/transformation_references", payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries(transformationReferencesAdminQueryKey);
        },
    });
};

export const useUpdateTransformationReference = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, payload }: { id: number; payload: TransformationReferenceWrite }) => {
            return await sureApiCall.put(`/api/transformation_references/${id}`, payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries(transformationReferencesAdminQueryKey);
        },
    });
};

export const useDeleteTransformationReference = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: number) => {
            return await sureApiCall.delete(`/api/transformation_references/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries(transformationReferencesAdminQueryKey);
        },
    });
};
